import { computed, inject, Injectable } from '@angular/core';
import { IProductsState, IPortfolioInstrumentRatios, IPortfolioDetailChartParams, INewInvestmentProgress, IEntryFeeCalcInput, IEntryFeeCalculationIn, IModelPortfolioChartInput, IModelStrategyChartInput, INewPortfolioInput, IInstrumentDetailFullOut, INewStrategyInput, IModelStrategyInstrumentOut, IBackcastingChartInput, IEditInvestmentProgress, IUpdatePortfolioV2Input, IUpdateStrategyInput, IModelPortfolioOut, IInvestmentModelInstrument, IPortfolioDraftsCheckOptions, IDraftPortfolioOut, IUpdateEfektikaStrategyInput, IInvestmentInstrumentRemoval, EWithdrawalPortfolioType } from 'src/models/products.model';
import { IInvestmentModelChartOut, IChartPointValue, IBackcastingChartOut, IInstrumentChartInput, IInstrumentChartOut, IInstrumentChartPoint } from 'src/models/charts.model';
import { signalStore, withState, withMethods, withComputed } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, forkJoin, from, map, mergeMap, Observable, of, pipe, switchMap, tap, toArray } from 'rxjs';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { withDevtools, updateState } from '@angular-architects/ngrx-toolkit';
import { RestApiService } from 'src/services/rest-api.service';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';
import { EInstrumentCategory, EManagementType, IImageUploadProps, IPageRequest } from 'src/models/common.model';
import { UserDataService } from '../user-data.service';
import { LoggerService } from 'src/services/logger.service';

enum EProductsActions {
  PORTFOLIOS_REQUEST = '[Products] Portfolios Request',
  PORTFOLIOS_SUCCESS = '[Products] Portfolios Success',
  PORTFOLIOS_FAILURE = '[Products] Portfolios Failure',
  ALL_INVESTMENTS_REQUEST = '[Products] All Investments Request',
  ALL_INVESTMENTS_SUCCESS = '[Products] All Investments Success',
  ALL_INVESTMENTS_FAILURE = '[Products] All Investments Failure',
  SELECTED_PORTFOLIO = '[Products] Selected Portfolio',
  CLEAR_SELECTED_PORTFOLIO = '[Products] Clear Selected Portfolio',
  PORTFOLIO_DETAIL_REQUEST = '[Products] Portfolio Detail Request',
  PORTFOLIO_DETAIL_SUCCESS = '[Products] Portfolio Detail Success',
  PORTFOLIO_DETAIL_FAILURE = '[Products] Portfolio Detail Failure',
  PORTFOLIO_FEE_CONFIGS_REQUEST = '[Products] Portfolio Fee Configs Request',
  PORTFOLIO_FEE_CONFIGS_SUCCESS = '[Products] Portfolio Fee Configs Success',
  PORTFOLIO_FEE_CONFIGS_FAILURE = '[Products] Portfolio Fee Configs Failure',
  MODEL_PORTFOLIO_REQUEST = '[Products] Model Portfolio Request',
  MODEL_PORTFOLIO_SUCCESS = '[Products] Model Portfolio Success',
  MODEL_PORTFOLIO_FAILURE = '[Products] Model Portfolio Failure',
  CLEAR_MODEL_PORTFOLIO = '[Products] Clear Model Portfolio',
  MODEL_PORTFOLIO_BY_RISK_SCORE_REQUEST = '[Products] Model Portfolio By Risk Score Request',
  MODEL_PORTFOLIO_BY_RISK_SCORE_SUCCESS = '[Products] Model Portfolio By Risk Score Success',
  MODEL_PORTFOLIO_BY_RISK_SCORE_FAILURE = '[Products] Model Portfolio By Risk Score Failure',
  PORTFOLIO_INSTRUMENT_RATIOS_REQUEST = '[Products] Portfolio Instrument Ratios Request',
  PORTFOLIO_INSTRUMENT_RATIOS_SUCCESS = '[Products] Portfolio Instrument Ratios Success',
  PORTFOLIO_INSTRUMENT_RATIOS_FAILURE = '[Products] Portfolio Instrument Ratios Failure',
  INVESTMENT_INSTRUMENT_REMOVALS_REQUEST = '[Products] Investment Instrument Removals Request',
  INVESTMENT_INSTRUMENT_REMOVALS_SUCCESS = '[Products] Investment Instrument Removals Success',
  INVESTMENT_INSTRUMENT_REMOVALS_FAILURE = '[Products] Investment Instrument Removals Failure',
  CLEAR_INVESTMENT_INSTRUMENT_REMOVALS = '[Products] Clear Investment Instrument Removals',
  PORTFOLIO_HISTORY_CHART_REQUEST = '[Products] Portfolio History Chart Request',
  PORTFOLIO_HISTORY_CHART_SUCCESS = '[Products] Portfolio History Chart Success',
  PORTFOLIO_HISTORY_CHART_FAILURE = '[Products] Portfolio History Chart Failure',
  CLEAR_PORTFOLIO_HISTORY_CHART = '[Products] Clear Portfolio History Chart',
  PORTFOLIO_FUTURE_DEVELOPMENT_CHART_REQUEST = '[Products] Portfolio Future Development Chart Request',
  PORTFOLIO_FUTURE_DEVELOPMENT_CHART_SUCCESS = '[Products] Portfolio Future Development Chart Success',
  PORTFOLIO_FUTURE_DEVELOPMENT_CHART_FAILURE = '[Products] Portfolio Future Development Chart Failure',
  STRATEGY_FUTURE_DEVELOPMENT_CHART_REQUEST = '[Products] Strategy Future Development Chart Request',
  STRATEGY_FUTURE_DEVELOPMENT_CHART_SUCCESS = '[Products] Strategy Future Development Chart Success',
  STRATEGY_FUTURE_DEVELOPMENT_CHART_FAILURE = '[Products] Strategy Future Development Chart Failure',
  BACKCASTING_CHART_REQUEST = '[Products] Backcasting Chart Request',
  BACKCASTING_CHART_SUCCESS = '[Products] Backcasting Chart Success',
  BACKCASTING_CHART_FAILURE = '[Products] Backcasting Chart Failure',
  BACKCASTING_CHART_CLEAR = '[Products] Backcasting Chart Clear',
  INSTRUMENT_CHART_REQUEST = '[Products] Instrument Chart Request',
  INSTRUMENT_CHART_SUCCESS = '[Products] Instrument Chart Success',
  INSTRUMENT_CHART_FAILURE = '[Products] Instrument Chart Failure',
  INSTRUMENT_CHART_CLEAR = '[Products] Instrument Chart Clear',
  ENTRY_FEE_CALCULATION_NEW_INVESTMENT_REQUEST = '[Products] Entry Fee Calculation New Investment Request',
  ENTRY_FEE_CALCULATION_NEW_INVESTMENT_SUCCESS = '[Products] Entry Fee Calculation New Investment Success',
  ENTRY_FEE_CALCULATION_NEW_INVESTMENT_FAILURE = '[Products] Entry Fee Calculation New Investment Failure',
  ENTRY_FEE_CALCULATION_NEW_INVESTMENT_TERMINATED = '[Products] Entry Fee Calculation New Investment Terminated',
  ENTRY_FEE_CALCULATION_EXISTING_INVESTMENT_REQUEST = '[Products] Entry Fee Calculation Existing Investment Request',
  ENTRY_FEE_CALCULATION_EXISTING_INVESTMENT_SUCCESS = '[Products] Entry Fee Calculation Existing Investment Success',
  ENTRY_FEE_CALCULATION_EXISTING_INVESTMENT_FAILURE = '[Products] Entry Fee Calculation Existing Investment Failure',
  ENTRY_FEE_CALCULATION_EXISTING_INVESTMENT_TERMINATED = '[Products] Entry Fee Calculation Existing Investment Terminated',
  CLEAR_ENTRY_FEE_TO_DISPLAY = '[Products] Clear Entry Fee To Display',
  SET_NEW_INVESTMENT_PROGRESS = '[Products] Set New Investment Progress',
  CLEAR_NEW_INVESTMENT_PROGRESS = '[Products] Clear New Investment Progress',
  SET_EDIT_INVESTMENT_PROGRESS = '[Products] Set Edit Investment Progress',
  CLEAR_EDIT_INVESTMENT_PROGRESS = '[Products] Clear Edit Investment Progress',
  CREATE_NEW_PORTFOLIO_REQUEST = '[Products] Create New Portfolio Request',
  CREATE_NEW_PORTFOLIO_SUCCESS = '[Products] Create New Portfolio Success',
  CREATE_NEW_PORTFOLIO_FAILURE = '[Products] Create New Portfolio Failure',
  CREATE_NEW_STRATEGY_REQUEST = '[Products] Create New Strategy Request',
  CREATE_NEW_STRATEGY_SUCCESS = '[Products] Create New Strategy Success',
  CREATE_NEW_STRATEGY_FAILURE = '[Products] Create New Strategy Failure',
  UPDATE_PORTFOLIO_REQUEST = '[Products] Update Portfolio Request',
  UPDATE_PORTFOLIO_SUCCESS = '[Products] Update Portfolio Success',
  UPDATE_PORTFOLIO_FAILURE = '[Products] Update Portfolio Failure',
  UPDATE_STRATEGY_REQUEST = '[Products] Update Strategy Request',
  UPDATE_STRATEGY_SUCCESS = '[Products] Update Strategy Success',
  UPDATE_STRATEGY_FAILURE = '[Products] Update Strategy Failure',
  UPDATE_EFEKTIKA_STRATEGY_REQUEST = '[Products] Update Efektika Strategy Request',
  UPDATE_EFEKTIKA_STRATEGY_SUCCESS = '[Products] Update Efektika Strategy Success',
  UPDATE_EFEKTIKA_STRATEGY_FAILURE = '[Products] Update Efektika Strategy Failure',
  UPDATE_PORTFOLIO_PHOTO_REQUEST = '[Products] Update Portfolio Photo Request',
  UPDATE_PORTFOLIO_PHOTO_SUCCESS = '[Products] Update Portfolio Photo Success',
  UPDATE_PORTFOLIO_PHOTO_FAILURE = '[Products] Update Portfolio Photo Failure',
  DELETE_INVESTMENT_PHOTO_REQUEST = '[Products] Delete Investment Photo Request',
  DELETE_INVESTMENT_PHOTO_SUCCESS = '[Products] Delete Investment Photo Success',
  DELETE_INVESTMENT_PHOTO_FAILURE = '[Products] Delete Investment Photo Failure',
  AVAILABLE_INSTRUMENTS_REQUEST = '[Products] Available Instruments Request',
  AVAILABLE_INSTRUMENTS_SUCCESS = '[Products] Available Instruments Success',
  AVAILABLE_INSTRUMENTS_FAILURE = '[Products] Available Instruments Failure',
  PUBLIC_INSTRUMENTS_REQUEST = '[Products] Public Instruments Request',
  PUBLIC_INSTRUMENTS_SUCCESS = '[Products] Public Instruments Success',
  PUBLIC_INSTRUMENTS_FAILURE = '[Products] Public Instruments Failure',
  THEMATIC_STRATEGIES_REQUEST = '[Products] Thematic Strategies Request',
  THEMATIC_STRATEGIES_SUCCESS = '[Products] Thematic Strategies Success',
  THEMATIC_STRATEGIES_FAILURE = '[Products] Thematic Strategies Failure',
  CLEAR_THEMATIC_STRATEGIES = '[Products] Clear Thematic Strategies',
  DRAFT_INVESTMENTS_LIST_REQUEST = '[Products] Draft Investments List Request',
  DRAFT_INVESTMENTS_LIST_RESPONSE = '[Products] Draft Investments List Response',
  DRAFT_INVESTMENTS_LIST_FAILURE = '[Products] Draft Investments List Failure',
  DRAFT_INVESTMENTS_LIST_CLEAR = '[Products] Draft Investments List Clear',
  INVESTMENT_CHANGE_DRAFTS_LIST_REQUEST = '[Products] Investment Change Drafts List Request',
  INVESTMENT_CHANGE_DRAFTS_LIST_RESPONSE = '[Products] Investment Change Drafts List Response',
  INVESTMENT_CHANGE_DRAFTS_LIST_FAILURE = '[Products] Investment Change Drafts List Failure',
  INVESTMENT_CHANGE_DRAFTS_LIST_CLEAR = '[Products] Investment Change Drafts List Clear',
  CASH_INSTRUMENTS_REQUEST = '[Products] Cash Instruments Request',
  CASH_INSTRUMENTS_SUCCESS = '[Products] Cash Instruments Success',
  CASH_INSTRUMENTS_FAILURE = '[Products] Cash Instruments Failure',
  STRATEGY_LIMITS_REQUEST = '[Products] Strategy Limits Request',
  STRATEGY_LIMITS_SUCCESS = '[Products] Strategy Limits Success',
  STRATEGY_LIMITS_FAILURE = '[Products] Strategy Limits Failure',
  MODEL_STRATEGY_REQUEST = '[Products] Model Strategy Request',
  MODEL_STRATEGY_SUCCESS = '[Products] Model Strategy Success',
  MODEL_STRATEGY_FAILURE = '[Products] Model Strategy Failure',
  CLEAR_MODEL_STRATEGY = '[Products] Clear Model Strategy',
  DEPOSIT_INFO_REQUEST = '[Products] Deposit info Request',
  DEPOSIT_INFO_SUCCESS = '[Products] Deposit info Success',
  DEPOSIT_INFO_FAILURE = '[Products] Deposit info Failure',
  DEPOSIT_INFO_CLEAR = '[Products] Deposit info Clear',
  WITHDRAWAL_REQUEST = '[Products] Withdrawal Request',
  WITHDRAWAL_SUCCESS = '[Products] Withdrawal Success',
  WITHDRAWAL_FAILURE = '[Products] Withdrawal Failure',
  WITHDRAWAL_PORTFOLIOS_REQUEST = '[Products] Withdrawal Portfolios Request',
  WITHDRAWAL_PORTFOLIOS_SUCCESS = '[Products] Withdrawal Portfolios Success',
  WITHDRAWAL_PORTFOLIOS_FAILURE = '[Products] Withdrawal Portfolios Failure',
}

const initialProductsState: IProductsState = {
  portfolios: [],
  allInvestments: [],
  selectedPortfolio: null,
  selectedDraftPortfolio: null,
  portfoliosTotal: null,
  portfolioFeeConfigs: null,
  portfolioHistoryChartData: null,
  modelPortfolio: null,
  modelPortfolioByRiskScore: null,
  investmentFutureDevelopmentChart: null,
  instrumentRatios: null,
  entryFeeToDisplay: null,
  newInvestmentProgress: null,
  editInvestmentProgress: null,
  availableInstruments: [],
  instrumentRegions: [],
  publicInstruments: [],
  publicInstrumentRegions: [],
  thematicStrategies: [],
  draftInvestments: [],
  investmentChangeDrafts: [],
  cashInstruments: [],
  strategyLimits: null,
  selectedModelStrategy: null,
  backcastingChart: null,
  instrumentChart: null,
  depositInfo: null,
  selectedPortfolioInstrumentRemovals: [],
};

const STORE_NAME = 'products';

@Injectable({ providedIn: 'root' })
export class ProductsStore extends signalStore(
  withState(initialProductsState),
  withDevtools(STORE_NAME),
  withRequestStatus(),
  withComputed((store, userDataService = inject(UserDataService)) => ({
    userCashInstrument: computed<IInstrumentDetailFullOut | null>(() => {
      const foundInstrument = store.cashInstruments()
        .find(instrument => instrument.currencyCode === userDataService.userCurrencyCode()) as IInstrumentDetailFullOut | undefined;
      if (foundInstrument) foundInstrument.instrumentCategory = EInstrumentCategory.MONEY;
      return foundInstrument ?? null;
    })
  })),
   withMethods((store, restApiService = inject(RestApiService), userDataService = inject(UserDataService), logger = inject(LoggerService)) => {

    const getAllInvestments = rxMethod<void>(
      pipe(
        tap(() =>{
          updateState(store, EProductsActions.ALL_INVESTMENTS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.ALL_INVESTMENTS, loadingProps)
          }));
        }),
        switchMap((params) =>
          restApiService.getAllInvestments().pipe(
            map((response) =>
              updateState(store, EProductsActions.ALL_INVESTMENTS_SUCCESS, ({ loadingProps, errorProps }) => ({
                allInvestments: response,
                ...withoutLoading(ELoadingEntity.ALL_INVESTMENTS, loadingProps),
                ...withoutError(ELoadingEntity.ALL_INVESTMENTS, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.ALL_INVESTMENTS_FAILURE, ({ loadingProps, errorProps }) => ({
                  allInvestments: [],
                  ...withoutLoading(ELoadingEntity.ALL_INVESTMENTS, loadingProps),
                  ...withError(ELoadingEntity.ALL_INVESTMENTS, { error, errorParams: { params }}, errorProps)
                }))
              )
            )
          )
        )
      )
    );
    const getPortfolioOverviews = rxMethod<IPageRequest>(
      pipe(
        tap(() => {
          updateState(store, EProductsActions.PORTFOLIOS_REQUEST, ({ loadingProps, errorProps }) => ({
            ...withLoading(ELoadingEntity.PORTFOLIO_OVERVIEW_LIST, loadingProps),
            ...withoutError(ELoadingEntity.PORTFOLIO_OVERVIEW_LIST, errorProps),
          }));
        }),
        switchMap((params) =>
          restApiService.getPortfolioOverviews(params).pipe(
            map((response) =>
              updateState(store, EProductsActions.PORTFOLIOS_SUCCESS, ({ loadingProps, errorProps }) => ({
                portfolios: response.content,
                portfoliosTotal: response.totalCount,
                ...withoutLoading(ELoadingEntity.PORTFOLIO_OVERVIEW_LIST, loadingProps),
                ...withoutError(ELoadingEntity.PORTFOLIO_OVERVIEW_LIST, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.PORTFOLIOS_FAILURE, ({ loadingProps, errorProps }) => ({
                  portfolios: [],
                  ...withoutLoading(ELoadingEntity.PORTFOLIO_OVERVIEW_LIST, loadingProps),
                  ...withError(ELoadingEntity.PORTFOLIO_OVERVIEW_LIST, { error, errorParams: { params } }, errorProps)
                }))
              )
            )
          )
        )
      )
    );

    const getWithdrawalPortfolios = rxMethod<{ params: IPageRequest; type: EWithdrawalPortfolioType }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.WITHDRAWAL_PORTFOLIOS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.WITHDRAWAL_PORTFOLIOS_LIST, loadingProps),
          }))
        ),
        switchMap(({ params, type }) =>
          restApiService.getWithdrawalPortfolios(params, type).pipe(
            map((response) =>
              updateState(store, EProductsActions.WITHDRAWAL_PORTFOLIOS_SUCCESS, ({ loadingProps, errorProps }) => ({
                portfolios: response.content,
                portfoliosTotal: response.totalCount,
                ...withoutLoading(ELoadingEntity.WITHDRAWAL_PORTFOLIOS_LIST, loadingProps),
                ...withoutError(ELoadingEntity.WITHDRAWAL_PORTFOLIOS_LIST, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.WITHDRAWAL_PORTFOLIOS_FAILURE, ({ loadingProps, errorProps }) => ({
                  portfolios: [],
                  ...withoutLoading(ELoadingEntity.WITHDRAWAL_PORTFOLIOS_LIST, loadingProps),
                  ...withError(ELoadingEntity.WITHDRAWAL_PORTFOLIOS_LIST, { error, errorParams: null }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const clearSelectedPortfolio = () => {
      updateState(store, EProductsActions.CLEAR_SELECTED_PORTFOLIO, ({ errorProps }) => ({
        selectedPortfolio: null,
        ...withoutError(ELoadingEntity.SELECTED_PORTFOLIO_DETAIL, errorProps)
      }));
    }

    const clearModelPortfolio = () => {
      updateState(store, EProductsActions.CLEAR_MODEL_PORTFOLIO, { modelPortfolio: null });
    }

    const getModelStrategy = rxMethod<{ modelStrategyId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.MODEL_STRATEGY_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.MODEL_STRATEGY, loadingProps),
          }))
        ),
        switchMap(({ modelStrategyId }) =>
          restApiService.getModelStrategy(modelStrategyId).pipe(
            map((response) =>
              updateState(store, EProductsActions.MODEL_STRATEGY_SUCCESS, ({ loadingProps, errorProps }) => {
                const userInstrumentCategories = userDataService.userInstrumentCategories();
                const filteredInstruments = response.instruments.filter((i: IModelStrategyInstrumentOut) => userInstrumentCategories
                    // Each instrument's category must be present in allowed instrument categories and instrument must not be marked for removal
                    .some(ic => ic === i.instrument.instrumentCategory) && !i.instrument.willBeRemoved) as IModelStrategyInstrumentOut[] ?? [];
                return {
                  selectedModelStrategy: {
                    ...response,
                    instruments: filteredInstruments,
                  },
                  ...withoutLoading(ELoadingEntity.MODEL_STRATEGY, loadingProps),
                  ...withoutError(ELoadingEntity.MODEL_STRATEGY, errorProps),
                }})
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.MODEL_STRATEGY_FAILURE, ({ loadingProps, errorProps }) => ({
                  selectedModelStrategy: null,
                  ...withoutLoading(ELoadingEntity.MODEL_STRATEGY, loadingProps),
                  ...withError(
                    ELoadingEntity.MODEL_STRATEGY,
                    {
                      error: error,
                      errorParams: { modelStrategyId },
                    },
                    errorProps
                  ),
                }))
              )
            )
          )
        )
      )
    );

    const clearModelStrategy = () => {
      updateState(store, EProductsActions.CLEAR_MODEL_STRATEGY, { selectedModelStrategy: null });
    }

    const getSelectedPortfolio = rxMethod<{ portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.PORTFOLIO_DETAIL_REQUEST, ({ loadingProps, errorProps }) => ({
            ...withLoading(ELoadingEntity.SELECTED_PORTFOLIO_DETAIL, loadingProps),
            ...withoutError(ELoadingEntity.SELECTED_PORTFOLIO_DETAIL, errorProps),
          }))
        ),
        switchMap(({ portfolioId }) =>
          restApiService.getSelectedPortfolio(portfolioId).pipe(
            map((response) =>
              updateState(store, EProductsActions.PORTFOLIO_DETAIL_SUCCESS, ({ loadingProps, errorProps }) => ({
                selectedPortfolio: response,
                ...withoutLoading(ELoadingEntity.SELECTED_PORTFOLIO_DETAIL, loadingProps),
                ...withoutError(ELoadingEntity.SELECTED_PORTFOLIO_DETAIL, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.PORTFOLIO_DETAIL_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.SELECTED_PORTFOLIO_DETAIL, loadingProps),
                  ...withError(ELoadingEntity.SELECTED_PORTFOLIO_DETAIL, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getPortfolioFeeConfigs = rxMethod<{ portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.PORTFOLIO_FEE_CONFIGS_REQUEST, ({ loadingProps, errorProps }) => ({
            ...withLoading(ELoadingEntity.PORTFOLIO_FEE_CONFIGS, loadingProps),
            ...withoutError(ELoadingEntity.PORTFOLIO_FEE_CONFIGS, errorProps),
          }))
        ),
        switchMap(({ portfolioId }) =>
          restApiService.getPortfolioFeeConfigs(portfolioId).pipe(
            map((response) =>
              updateState(store, EProductsActions.PORTFOLIO_FEE_CONFIGS_SUCCESS, ({ loadingProps, errorProps }) => ({
                portfolioFeeConfigs: { portfolioId, feeConfigs: response },
                ...withoutLoading(ELoadingEntity.PORTFOLIO_FEE_CONFIGS, loadingProps),
                ...withoutError(ELoadingEntity.PORTFOLIO_FEE_CONFIGS, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.PORTFOLIO_FEE_CONFIGS_FAILURE, ({ loadingProps, errorProps }) => ({
                  portfolioFeeConfigs: null,
                  ...withoutLoading(ELoadingEntity.PORTFOLIO_FEE_CONFIGS, loadingProps),
                  ...withError(ELoadingEntity.PORTFOLIO_FEE_CONFIGS, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getModelPortfolio = rxMethod<{ portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.MODEL_PORTFOLIO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.MODEL_PORTFOLIO, loadingProps),
          }))
        ),
        switchMap(({ portfolioId }) =>
          restApiService.getModelPortfolio(portfolioId).pipe(
            map((response) =>
              updateState(store, EProductsActions.MODEL_PORTFOLIO_SUCCESS, ({ loadingProps, errorProps }) => ({
                modelPortfolio: response,
                ...withoutLoading(ELoadingEntity.MODEL_PORTFOLIO, loadingProps),
                ...withoutError(ELoadingEntity.MODEL_PORTFOLIO, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.MODEL_PORTFOLIO_FAILURE, ({ loadingProps, errorProps }) => ({
                  modelPortfolio: null,
                  ...withoutLoading(ELoadingEntity.MODEL_PORTFOLIO, loadingProps),
                  ...withError(ELoadingEntity.MODEL_PORTFOLIO, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getPortfolioInstrumentRatios = rxMethod<{ portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.PORTFOLIO_INSTRUMENT_RATIOS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.PORTFOLIO_INSTRUMENT_RATIOS, loadingProps),
          }))
        ),
        switchMap(({ portfolioId }) =>
          restApiService.getPortfolioInstrumentRatios(portfolioId).pipe(
            map((response: IPortfolioInstrumentRatios) => {
              return updateState(store, EProductsActions.PORTFOLIO_INSTRUMENT_RATIOS_SUCCESS, ({ loadingProps, errorProps }) => ({
                instrumentRatios: response,
                ...withoutLoading(ELoadingEntity.PORTFOLIO_INSTRUMENT_RATIOS, loadingProps),
                ...withoutError(ELoadingEntity.PORTFOLIO_INSTRUMENT_RATIOS, errorProps),
              }));
            }),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.PORTFOLIO_INSTRUMENT_RATIOS_FAILURE, ({ loadingProps, errorProps }) => ({
                  instrumentRatios: null,
                  ...withoutLoading(ELoadingEntity.PORTFOLIO_INSTRUMENT_RATIOS, loadingProps),
                  ...withError(ELoadingEntity.PORTFOLIO_INSTRUMENT_RATIOS, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getInvestmentInstrumentRemovals = rxMethod<{ investmentId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.INVESTMENT_INSTRUMENT_REMOVALS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.INVESTMENT_INSTRUMENT_REMOVALS, loadingProps),
          }))
        ),
        switchMap(({ investmentId }) =>
          restApiService.getInvestmentInstrumentRemovals(investmentId).pipe(
            map((response: IInvestmentInstrumentRemoval[]) =>
              updateState(store, EProductsActions.INVESTMENT_INSTRUMENT_REMOVALS_SUCCESS, ({ loadingProps, errorProps }) => ({
                selectedPortfolioInstrumentRemovals: response,
                ...withoutLoading(ELoadingEntity.INVESTMENT_INSTRUMENT_REMOVALS, loadingProps),
                ...withoutError(ELoadingEntity.INVESTMENT_INSTRUMENT_REMOVALS, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.INVESTMENT_INSTRUMENT_REMOVALS_FAILURE, ({ loadingProps, errorProps }) => ({
                  selectedPortfolioInstrumentRemovals: [],
                  ...withoutLoading(ELoadingEntity.INVESTMENT_INSTRUMENT_REMOVALS, loadingProps),
                  ...withError(ELoadingEntity.INVESTMENT_INSTRUMENT_REMOVALS, { error, errorParams: { investmentId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const clearInvestmentInstrumentRemovals = () => {
      updateState(store, EProductsActions.CLEAR_INVESTMENT_INSTRUMENT_REMOVALS, ({ errorProps }) => ({
        selectedPortfolioInstrumentRemovals: [],
        ...withoutError(ELoadingEntity.INVESTMENT_INSTRUMENT_REMOVALS, errorProps),
      }));
    };

    const getPortfolioHistoryChartData = rxMethod<{ params: IPortfolioDetailChartParams; portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.PORTFOLIO_HISTORY_CHART_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.PORTFOLIO_HISTORY_CHART, loadingProps),
          }))
        ),
        switchMap(({ params, portfolioId }) =>
          restApiService.getInvestmentChart(params, portfolioId).pipe(
            map((response) =>
              updateState(store, EProductsActions.PORTFOLIO_HISTORY_CHART_SUCCESS, ({ loadingProps, errorProps }) => ({
                portfolioHistoryChartData: response,
                ...withoutLoading(ELoadingEntity.PORTFOLIO_HISTORY_CHART, loadingProps),
                ...withoutError(ELoadingEntity.PORTFOLIO_HISTORY_CHART, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.PORTFOLIO_HISTORY_CHART_FAILURE, ({ loadingProps, errorProps }) => ({
                  portfolioHistoryChartData: null,
                  ...withoutLoading(ELoadingEntity.PORTFOLIO_HISTORY_CHART, loadingProps),
                  ...withError(ELoadingEntity.PORTFOLIO_HISTORY_CHART, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getModelPortfolioFutureDevelopmentChart = rxMethod<{ params: IModelPortfolioChartInput }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.PORTFOLIO_FUTURE_DEVELOPMENT_CHART_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.PORTFOLIO_FUTURE_DEVELOPMENT_CHART, loadingProps),
          }))
        ),
        switchMap(({ params }) =>
          restApiService.getModelPortfolioChart(params).pipe(
            map((response: IInvestmentModelChartOut) => {
              // Convert Unix time from seconds to milliseconds for chart compatibility
              const convertSeries = (series: IChartPointValue[]): IChartPointValue[] =>
                series.map(point => ({
                  x: point.x * 1000, // backend provides Unix time in seconds, convert to ms for chart
                  y: point.y,
                }));

              const convertedResponse: IInvestmentModelChartOut = {
                optimistic: convertSeries(response.optimistic),
                expected: convertSeries(response.expected),
                pessimistic: convertSeries(response.pessimistic),
                pureInvestment: convertSeries(response.pureInvestment),
              };

              return updateState(store, EProductsActions.PORTFOLIO_FUTURE_DEVELOPMENT_CHART_SUCCESS, ({ loadingProps, errorProps }) => ({
                investmentFutureDevelopmentChart: convertedResponse,
                ...withoutLoading(ELoadingEntity.PORTFOLIO_FUTURE_DEVELOPMENT_CHART, loadingProps),
                ...withoutError(ELoadingEntity.PORTFOLIO_FUTURE_DEVELOPMENT_CHART, errorProps),
              }));
            }),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.PORTFOLIO_FUTURE_DEVELOPMENT_CHART_FAILURE, ({ loadingProps, errorProps }) => ({
                  investmentFutureDevelopmentChart: null,
                  ...withoutLoading(ELoadingEntity.PORTFOLIO_FUTURE_DEVELOPMENT_CHART, loadingProps),
                  ...withError(ELoadingEntity.PORTFOLIO_FUTURE_DEVELOPMENT_CHART, { error, errorParams: { riskProfile: params.riskProfile } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const clearBackcastingChart = () => {
      updateState(store, EProductsActions.BACKCASTING_CHART_CLEAR, {
        backcastingChart: null
      });
    }

    const getBackcastingChart = rxMethod<{
      params: IBackcastingChartInput | IBackcastingChartInput;
    }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.BACKCASTING_CHART_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.BACKCASTING_CHART, loadingProps),
          }))
        ),
        switchMap(({ params }) => {
            const directParams: IBackcastingChartInput = {
              ...params,
            } as IBackcastingChartInput;

            return restApiService.getBackcastingChart(directParams).pipe(
              map((response: IBackcastingChartOut) => {
                const convertedChartPoints = convertChartResponse(response.graph);

                return updateState(store, EProductsActions.BACKCASTING_CHART_SUCCESS, ({ loadingProps, errorProps }) => ({
                  backcastingChart: { ...response, graph: convertedChartPoints },
                  ...withoutLoading(ELoadingEntity.BACKCASTING_CHART, loadingProps),
                  ...withoutError(ELoadingEntity.BACKCASTING_CHART, errorProps),
                }));
              }),
              catchError((error) =>
                of(
                  updateState(store, EProductsActions.BACKCASTING_CHART_FAILURE, ({ loadingProps, errorProps }) => ({
                    backcastingChart: null,
                    ...withoutLoading(ELoadingEntity.BACKCASTING_CHART, loadingProps),
                    ...withError(ELoadingEntity.BACKCASTING_CHART, { error, errorParams: {} }, errorProps),
                  }))
                )
              )
            );
        })
      )
    );

    const clearInstrumentChart = () => {
      updateState(store, EProductsActions.INSTRUMENT_CHART_CLEAR, {
        instrumentChart: null
      });
    }

    const getInstrumentChart = rxMethod<{
      params: IInstrumentChartInput;
    }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.INSTRUMENT_CHART_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.INSTRUMENT_CHART, loadingProps),
          }))
        ),
        switchMap(({ params }) => {
            const directParams: IInstrumentChartInput = {
              ...params,
            } as IInstrumentChartInput;

            return restApiService.instrumentChart(directParams).pipe(
              map((response: IInstrumentChartOut) => {
                const convertedChartPoints = convertChartResponse(response.priceInTime);

                return updateState(store, EProductsActions.INSTRUMENT_CHART_SUCCESS, ({ loadingProps, errorProps }) => ({
                  instrumentChart: { ...response, priceInTime: convertedChartPoints },
                  ...withoutLoading(ELoadingEntity.INSTRUMENT_CHART, loadingProps),
                  ...withoutError(ELoadingEntity.INSTRUMENT_CHART, errorProps),
                }));
              }),
              catchError((error) =>
                of(
                  updateState(store, EProductsActions.INSTRUMENT_CHART_FAILURE, ({ loadingProps, errorProps }) => ({
                    instrumentChart: null,
                    ...withoutLoading(ELoadingEntity.INSTRUMENT_CHART, loadingProps),
                    ...withError(ELoadingEntity.INSTRUMENT_CHART, { error, errorParams: {} }, errorProps),
                  }))
                )
              )
            );
        })
      )
    );

    // Helper functions to reduce duplication
    function convertChartResponse<T extends IChartPointValue | IInstrumentChartPoint>(datapoints: T[]): T[] {
      const convertSeries = (series: T[]): T[] =>
        series.map(point => ({
          ...point,
          x: point.x * 1000, // backend provides Unix time in seconds, convert to ms for chart
          y: point.y,
        }));

      return convertSeries(datapoints);
    }

    const getStrategyFutureDevelopmentChart = rxMethod<{ params: IModelStrategyChartInput }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.STRATEGY_FUTURE_DEVELOPMENT_CHART_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.STRATEGY_FUTURE_DEVELOPMENT_CHART, loadingProps),
          }))
        ),
        switchMap(({ params }) =>
          restApiService.getStrategyModelChart(params).pipe(
            map((response: IInvestmentModelChartOut) => {
              // Convert Unix time from seconds to milliseconds for chart compatibility
              const convertSeries = (series: IChartPointValue[]): IChartPointValue[] =>
                series.map(point => ({
                  x: point.x * 1000, // backend provides Unix time in seconds, convert to ms for chart
                  y: point.y,
                }));

              const convertedResponse: IInvestmentModelChartOut = {
                optimistic: convertSeries(response.optimistic),
                expected: convertSeries(response.expected),
                pessimistic: convertSeries(response.pessimistic),
                pureInvestment: convertSeries(response.pureInvestment),
              };

              return updateState(store, EProductsActions.STRATEGY_FUTURE_DEVELOPMENT_CHART_SUCCESS, ({ loadingProps, errorProps }) => ({
                investmentFutureDevelopmentChart: convertedResponse,
                ...withoutLoading(ELoadingEntity.STRATEGY_FUTURE_DEVELOPMENT_CHART, loadingProps),
                ...withoutError(ELoadingEntity.STRATEGY_FUTURE_DEVELOPMENT_CHART, errorProps),
              }));
            }),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.STRATEGY_FUTURE_DEVELOPMENT_CHART_FAILURE, ({ loadingProps, errorProps }) => ({
                  investmentFutureDevelopmentChart: null,
                  ...withoutLoading(ELoadingEntity.STRATEGY_FUTURE_DEVELOPMENT_CHART, loadingProps),
                  ...withError(ELoadingEntity.STRATEGY_FUTURE_DEVELOPMENT_CHART, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const clearPortfolioHistoryChartData = () => {
      updateState(store, EProductsActions.CLEAR_PORTFOLIO_HISTORY_CHART, { portfolioHistoryChartData: null });
    };

    const getModelPortfolioByRiskScore = rxMethod<{ riskScore: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.MODEL_PORTFOLIO_BY_RISK_SCORE_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.MODEL_PORTFOLIO_BY_RISK_SCORE, loadingProps),
          }))
        ),
        switchMap(({ riskScore }) =>
          restApiService.getModelPortfolioByRiskScore(riskScore).pipe(
            map((response) =>
              updateState(store, EProductsActions.MODEL_PORTFOLIO_BY_RISK_SCORE_SUCCESS, ({ loadingProps, errorProps }) => ({
                modelPortfolioByRiskScore: response,
                ...withoutLoading(ELoadingEntity.MODEL_PORTFOLIO_BY_RISK_SCORE, loadingProps),
                ...withoutError(ELoadingEntity.MODEL_PORTFOLIO_BY_RISK_SCORE, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.MODEL_PORTFOLIO_BY_RISK_SCORE_FAILURE, ({ loadingProps, errorProps }) => ({
                  modelPortfolioByRiskScore: null,
                  ...withoutLoading(ELoadingEntity.MODEL_PORTFOLIO_BY_RISK_SCORE, loadingProps),
                  ...withError(ELoadingEntity.MODEL_PORTFOLIO_BY_RISK_SCORE, { error, errorParams: { riskScore } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const containsEmptyValue = (changes: IEntryFeeCalcInput['changes']): boolean => {
      return changes.oneTimeDeposit === null ||
        isNaN(changes.oneTimeDeposit) ||
        changes.regularDeposit === null ||
        isNaN(changes.regularDeposit);
    };

    const calculateEntryFeeForNewInvestment = rxMethod<IEntryFeeCalcInput>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.ENTRY_FEE_CALCULATION_NEW_INVESTMENT_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.ENTRY_FEE_CALCULATION, loadingProps),
          }))
        ),
        switchMap((input) => {
          const { changes } = input;

          if (containsEmptyValue(changes)) {
            updateState(store, EProductsActions.ENTRY_FEE_CALCULATION_NEW_INVESTMENT_TERMINATED, ({ loadingProps }) => ({
              ...withoutLoading(ELoadingEntity.ENTRY_FEE_CALCULATION, loadingProps),
            }));
            return EMPTY;
          }

          const entryFeeInput: IEntryFeeCalculationIn = {
            frameworkContractId: changes.frameworkContractId,
            initialDeposit: changes.oneTimeDeposit,
            regularDeposit: changes.regularDeposit,
            instruments: changes.instrumentsWeights instanceof Map
              ? Object.fromEntries(changes.instrumentsWeights)
              : changes.instrumentsWeights,
            weeksBetweenInvestments: changes.depositRegularity,
            riskScore: +changes.riskProfile,
            totalYearsToInvest: changes.timeOfInvestment,
            feeConfigId: changes.feeConfigId, // drafts
          };

          return restApiService.calculateEntryFeeForNewInvestment(entryFeeInput).pipe(
            map((response) =>
              updateState(store, EProductsActions.ENTRY_FEE_CALCULATION_NEW_INVESTMENT_SUCCESS, ({ loadingProps, errorProps }) => ({
                entryFeeToDisplay: response,
                ...withoutLoading(ELoadingEntity.ENTRY_FEE_CALCULATION, loadingProps),
                ...withoutError(ELoadingEntity.ENTRY_FEE_CALCULATION, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.ENTRY_FEE_CALCULATION_NEW_INVESTMENT_FAILURE, ({ loadingProps, errorProps }) => ({
                  entryFeeToDisplay: null,
                  ...withoutLoading(ELoadingEntity.ENTRY_FEE_CALCULATION, loadingProps),
                  ...withError(ELoadingEntity.ENTRY_FEE_CALCULATION, { error, errorParams: { type: 'new-investment' } }, errorProps),
                }))
              )
            )
          );
        })
      )
    );

    const calculateEntryFeeForExistingInvestment = rxMethod<{investmentId: number, input: IEntryFeeCalcInput}>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.ENTRY_FEE_CALCULATION_EXISTING_INVESTMENT_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.ENTRY_FEE_CALCULATION, loadingProps),
          }))
        ),
        switchMap(({investmentId, input}) => {
          const { changes } = input;

          if (containsEmptyValue(changes)) {
            updateState(store, EProductsActions.ENTRY_FEE_CALCULATION_EXISTING_INVESTMENT_TERMINATED, ({ loadingProps }) => ({
              ...withoutLoading(ELoadingEntity.ENTRY_FEE_CALCULATION, loadingProps),
            }));
            return EMPTY;
          }

          const entryFeeInput: IEntryFeeCalculationIn = {
            frameworkContractId: changes.frameworkContractId,
            initialDeposit: changes.oneTimeDeposit,
            regularDeposit: changes.regularDeposit,
            instruments: changes.instrumentsWeights instanceof Map
              ? Object.fromEntries(changes.instrumentsWeights)
              : changes.instrumentsWeights,
            weeksBetweenInvestments: changes.depositRegularity,
            riskScore: +changes.riskProfile,
            totalYearsToInvest: changes.timeOfInvestment,
          };

          return restApiService.calculateEntryFeeForExistingInvestment(entryFeeInput, investmentId).pipe(
            map((response) =>
              updateState(store, EProductsActions.ENTRY_FEE_CALCULATION_EXISTING_INVESTMENT_SUCCESS, ({ loadingProps, errorProps }) => ({
                entryFeeToDisplay: response,
                ...withoutLoading(ELoadingEntity.ENTRY_FEE_CALCULATION, loadingProps),
                ...withoutError(ELoadingEntity.ENTRY_FEE_CALCULATION, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.ENTRY_FEE_CALCULATION_EXISTING_INVESTMENT_FAILURE, ({ loadingProps, errorProps }) => ({
                  entryFeeToDisplay: null,
                  ...withoutLoading(ELoadingEntity.ENTRY_FEE_CALCULATION, loadingProps),
                  ...withError(ELoadingEntity.ENTRY_FEE_CALCULATION, { error, errorParams: { type: 'existing-investment', investmentId } }, errorProps),
                }))
              )
            )
          );
        })
      )
    );

    const createNewPortfolio = rxMethod<INewPortfolioInput>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.CREATE_NEW_PORTFOLIO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.CREATE_NEW_PORTFOLIO, loadingProps),
          }))
        ),
        switchMap((portfolioData) =>
          restApiService.postPortfolio(portfolioData).pipe(
            switchMap((response) => {
              let photoUploadError = false;
              if (!portfolioData.coverPhoto) {
                return of({ ...response, photoUploadError });
              }
              return restApiService
                .requestUpdatePortfolioPhoto(response.portfolioId, portfolioData.coverPhoto.imageProps)
                .pipe(
                  switchMap(({ url }) =>
                    restApiService.minioRequest(url, 'PUT', 'blob', portfolioData.coverPhoto!.photo)
                  ),
                  switchMap(() => restApiService.confirmPortfolioPhotoUpdated(response.portfolioId)),
                  catchError(() => {
                    photoUploadError = true;
                    return of(null);
                  }),
                  map(() => ({ ...response, photoUploadError })),
                );
            }),
            map(({ photoUploadError, ...response }) =>
              updateState(store, EProductsActions.CREATE_NEW_PORTFOLIO_SUCCESS, ({ loadingProps, errorProps, newInvestmentProgress }) => ({
                newInvestmentProgress: newInvestmentProgress
                  ? { ...newInvestmentProgress!, newInvestmentId: response.portfolioId, photoUploadError }
                  : null,
                ...withoutLoading(ELoadingEntity.CREATE_NEW_PORTFOLIO, loadingProps),
                ...withoutError(ELoadingEntity.CREATE_NEW_PORTFOLIO, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.CREATE_NEW_PORTFOLIO_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.CREATE_NEW_PORTFOLIO, loadingProps),
                  ...withError(ELoadingEntity.CREATE_NEW_PORTFOLIO, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const createNewStrategy = rxMethod<INewStrategyInput>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.CREATE_NEW_STRATEGY_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.CREATE_NEW_STRATEGY, loadingProps),
          }))
        ),
        switchMap((strategyData) =>
          restApiService.postStrategy(strategyData).pipe(
            switchMap((response) => {
              let photoUploadError = false;

              if (!strategyData.coverPhoto) {
                return of(({ ...response, photoUploadError }));
              }

              return restApiService
                .requestUpdatePortfolioPhoto(response.portfolioId, strategyData.coverPhoto.imageProps)
                .pipe(
                  switchMap(({ url }) =>
                    restApiService.minioRequest(url, 'PUT', 'blob', strategyData.coverPhoto!.photo)
                  ),
                  switchMap(() => restApiService.confirmPortfolioPhotoUpdated(response.portfolioId)),
                  catchError(() => {
                    photoUploadError = true;
                    return of(null);
                  }),
                  map(() => ({ ...response, photoUploadError })),
                );
            }),
            map(({ photoUploadError, ...response }) =>
              updateState(store, EProductsActions.CREATE_NEW_STRATEGY_SUCCESS, ({ loadingProps, errorProps, newInvestmentProgress }) => ({
                newInvestmentProgress: newInvestmentProgress
                  ? { ...newInvestmentProgress!, newInvestmentId: response.portfolioId, photoUploadError }
                  : null,
                ...withoutLoading(ELoadingEntity.CREATE_NEW_STRATEGY, loadingProps),
                ...withoutError(ELoadingEntity.CREATE_NEW_STRATEGY, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.CREATE_NEW_STRATEGY_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.CREATE_NEW_STRATEGY, loadingProps),
                  ...withError(ELoadingEntity.CREATE_NEW_STRATEGY, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const updatePortfolio = rxMethod<{ input: IUpdatePortfolioV2Input, portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.UPDATE_PORTFOLIO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.UPDATE_PORTFOLIO, loadingProps),
          }))
        ),
        switchMap(({ input, portfolioId }) => {
          const { updatedCoverPhoto, deletePhoto, ...updatePayload } = input;
          const requests: Observable<unknown>[] = [];
          let photoDeleteError = false;
          let photoUploadError = false;

          if (input.deletePhoto) {
            requests.push(
              restApiService.deleteInvestmentPhoto(portfolioId)
              .pipe(catchError(() => {
                photoDeleteError = true;
                return of(null);
              }), map(() => ({ photoUploadError, photoDeleteError })))
            );
          } else if (input.updatedCoverPhoto) {
            const { imageProps, photo } = input.updatedCoverPhoto;
            requests.push(
              restApiService
                .requestUpdatePortfolioPhoto(portfolioId, imageProps)
                .pipe(
                  switchMap(({ url }) =>
                    restApiService.minioRequest(url, 'PUT', 'blob', photo)
                  ),
                  switchMap(() => restApiService.confirmPortfolioPhotoUpdated(portfolioId)),
                  catchError(() => {
                    photoUploadError = true;
                    return of(null);
                  }),
                  map(() => ({ photoUploadError, photoDeleteError })),
                )
            );
          }

          if (!input.deletePhoto && !input.updatedCoverPhoto) {
            requests.push(of({ photoUploadError: false, photoDeleteError: false }));
          }

          requests.push(restApiService.updatePortfolio(updatePayload, portfolioId));

          return forkJoin(requests).pipe(
            map(([ photoErrors ]) =>
              updateState(store, EProductsActions.UPDATE_PORTFOLIO_SUCCESS, ({ loadingProps, errorProps, editInvestmentProgress }) => ({
                  editInvestmentProgress: editInvestmentProgress
                    ? { ...editInvestmentProgress!, ...(photoErrors as { photoUploadError: boolean; photoDeleteError: boolean; }) }
                    : null,
                  ...withoutLoading(ELoadingEntity.UPDATE_PORTFOLIO, loadingProps),
                  ...withoutError(ELoadingEntity.UPDATE_PORTFOLIO, errorProps),
                }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.UPDATE_PORTFOLIO_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.UPDATE_PORTFOLIO, loadingProps),
                  ...withError(ELoadingEntity.UPDATE_PORTFOLIO, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          );
        })
      )
    );

    const updateStrategy = rxMethod<{ input: IUpdateStrategyInput, portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.UPDATE_STRATEGY_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.UPDATE_STRATEGY, loadingProps),
          }))
        ),
        switchMap(({ input, portfolioId }) => {
          const { updatedCoverPhoto, deletePhoto, ...updatePayload } = input;
          const requests: Observable<unknown>[] = [];
          let photoDeleteError = false;
          let photoUploadError = false;

          if (input.deletePhoto) {
            requests.push(
              restApiService.deleteInvestmentPhoto(portfolioId)
              .pipe(catchError(() => {
                photoDeleteError = true;
                return of(null);
              }), map(() => ({ photoUploadError, photoDeleteError })))
            );
          } else if (input.updatedCoverPhoto) {
            const { imageProps, photo } = input.updatedCoverPhoto;
            requests.push(
              restApiService
                .requestUpdatePortfolioPhoto(portfolioId, imageProps)
                .pipe(
                  switchMap(({ url }) =>
                    restApiService.minioRequest(url, 'PUT', 'blob', photo)
                  ),
                  switchMap(() => restApiService.confirmPortfolioPhotoUpdated(portfolioId)),
                  catchError(() => {
                    photoUploadError = true;
                    return of(null);
                  }),
                  map(() => ({ photoUploadError, photoDeleteError })),
                )
            );
          }

          if (!input.deletePhoto && !input.updatedCoverPhoto) {
            requests.push(of({ photoUploadError: false, photoDeleteError: false }));
          }

          requests.push(restApiService.updateStrategy(updatePayload, portfolioId));

          return forkJoin(requests).pipe(
            map(([ photoErrors ]) =>
              updateState(store, EProductsActions.UPDATE_STRATEGY_REQUEST, ({ loadingProps, errorProps, editInvestmentProgress }) => ({
                editInvestmentProgress: editInvestmentProgress
                ? { ...editInvestmentProgress!, ...(photoErrors as { photoUploadError: boolean; photoDeleteError: boolean; }) }
                : null,
                ...withoutLoading(ELoadingEntity.UPDATE_STRATEGY, loadingProps),
                ...withoutError(ELoadingEntity.UPDATE_STRATEGY, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.UPDATE_STRATEGY_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.UPDATE_STRATEGY, loadingProps),
                  ...withError(ELoadingEntity.UPDATE_STRATEGY, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          );
        })
      )
    );

    const updateEfektikaStrategy = rxMethod<{ input: IUpdateEfektikaStrategyInput, portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.UPDATE_EFEKTIKA_STRATEGY_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.UPDATE_EFEKTIKA_STRATEGY, loadingProps),
          }))
        ),
        switchMap(({ input, portfolioId }) => {
          const { updatedCoverPhoto, deletePhoto, ...updatePayload } = input;
          const requests: Observable<unknown>[] = [];
          let photoDeleteError = false;
          let photoUploadError = false;

          if (input.deletePhoto) {
            requests.push(
              restApiService.deleteInvestmentPhoto(portfolioId)
              .pipe(catchError(() => {
                photoDeleteError = true;
                return of(null);
              }), map(() => ({ photoUploadError, photoDeleteError })))
            );
          } else if (input.updatedCoverPhoto) {
            const { imageProps, photo } = input.updatedCoverPhoto;
            requests.push(
              restApiService
                .requestUpdatePortfolioPhoto(portfolioId, imageProps)
                .pipe(
                  switchMap(({ url }) =>
                    restApiService.minioRequest(url, 'PUT', 'blob', photo)
                  ),
                  switchMap(() => restApiService.confirmPortfolioPhotoUpdated(portfolioId)),
                  catchError(() => {
                    photoUploadError = true;
                    return of(null);
                  }),
                  map(() => ({ photoUploadError, photoDeleteError })),
                )
            );
          }

          if (!input.deletePhoto && !input.updatedCoverPhoto) {
            requests.push(of({ photoUploadError: false, photoDeleteError: false }));
          }

          requests.push(restApiService.updateEfektikaStrategy(updatePayload, portfolioId));

          return forkJoin(requests).pipe(
            map(([ photoErrors ]) =>
              updateState(store, EProductsActions.UPDATE_EFEKTIKA_STRATEGY_SUCCESS, ({ loadingProps, errorProps, editInvestmentProgress }) => ({
                editInvestmentProgress: editInvestmentProgress
                ? { ...editInvestmentProgress!, ...(photoErrors as { photoUploadError: boolean; photoDeleteError: boolean; }) }
                : null,
                ...withoutLoading(ELoadingEntity.UPDATE_EFEKTIKA_STRATEGY, loadingProps),
                ...withoutError(ELoadingEntity.UPDATE_EFEKTIKA_STRATEGY, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.UPDATE_EFEKTIKA_STRATEGY_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.UPDATE_EFEKTIKA_STRATEGY, loadingProps),
                  ...withError(ELoadingEntity.UPDATE_EFEKTIKA_STRATEGY, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          );
        })
      )
    );

    const clearEntryFeeToDisplay = () => {
      updateState(store, EProductsActions.CLEAR_ENTRY_FEE_TO_DISPLAY, { entryFeeToDisplay: null });
    }

    const setNewInvestmentProgress = (progress: INewInvestmentProgress) => {
      updateState(store, EProductsActions.SET_NEW_INVESTMENT_PROGRESS, { newInvestmentProgress: progress });
    };

    const setEditInvestmentProgress = (progress: IEditInvestmentProgress) => {
      updateState(store, EProductsActions.SET_EDIT_INVESTMENT_PROGRESS, { editInvestmentProgress: progress });
    };

    const updateNewInvestmentProgress = (updates: Partial<INewInvestmentProgress>) => {
      const currentProgress = store.newInvestmentProgress();
      const updatedProgress: INewInvestmentProgress = {
        goal: currentProgress?.goal ?? null,
        investmentType: currentProgress?.investmentType ?? null,
        name: currentProgress?.name ?? null,
        coverPhoto: currentProgress?.coverPhoto ?? null,
        investmentPeriodInput: currentProgress?.investmentPeriodInput ?? null,
        regularInvestmentFrequency: currentProgress?.regularInvestmentFrequency ?? null,
        initialInvestment: currentProgress && (currentProgress.initialInvestment === 0 || currentProgress.initialInvestment) ? currentProgress.initialInvestment : null,
        regularInvestment: currentProgress && (currentProgress.regularInvestment === 0 || currentProgress.regularInvestment) ? currentProgress.regularInvestment : null,
        riskScore: currentProgress?.riskScore ?? null,
        frameworkContractId: currentProgress?.frameworkContractId ?? null,
        isDip: currentProgress && typeof currentProgress.isDip === 'boolean' ? currentProgress.isDip : null,
        selectedInstruments: currentProgress?.selectedInstruments.length ? currentProgress.selectedInstruments : [],
        instrumentsWeights: currentProgress?.instrumentsWeights ?? {},
        rebalancing: currentProgress && typeof currentProgress.rebalancing === 'boolean' ? currentProgress.rebalancing : null,
        modelStrategyId: currentProgress?.modelStrategyId ?? null,
        newInvestmentId: currentProgress?.newInvestmentId ?? null,
        modelStrategyFlowType: currentProgress?.modelStrategyFlowType ?? null,
        firstInvestmentMode: currentProgress?.firstInvestmentMode ?? false,
        photoUploadError: currentProgress?.photoUploadError ?? false,
        ...updates,
      };
      updateState(store, EProductsActions.SET_NEW_INVESTMENT_PROGRESS, { newInvestmentProgress: updatedProgress });
    };

    const updateEditInvestmentProgress = (updates: Partial<IEditInvestmentProgress>) => {
      const currentProgress = store.editInvestmentProgress();
      if (!currentProgress) return;
      updateState(store, EProductsActions.SET_EDIT_INVESTMENT_PROGRESS, { editInvestmentProgress: { ...currentProgress, ...updates } });
    };

    const clearNewInvestmentProgress = () => {
      updateState(store, EProductsActions.CLEAR_NEW_INVESTMENT_PROGRESS, { newInvestmentProgress: null });
    };

    const clearEditInvestmentProgress = () => {
      updateState(store, EProductsActions.CLEAR_EDIT_INVESTMENT_PROGRESS, { editInvestmentProgress: null });
    };

    /**
     * 1. Get signed URL for updating portfolio photo by providing size and content-type
     * 2. Upload photo to Minio using signed URL
     * 3. Confirm to backend that photo has been successfully updated
     * 4. Get updated photo from Minio
     */
    const updatePortfolioPhoto = rxMethod<{ portfolioId: number, imageProps: IImageUploadProps, image: Blob }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.UPDATE_PORTFOLIO_PHOTO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.UPDATE_PORTFOLIO_PHOTO, loadingProps),
          }))
        ),
        switchMap(({ portfolioId, imageProps, image }) =>
          restApiService.requestUpdatePortfolioPhoto(portfolioId, imageProps).pipe(
            switchMap(({ url }) => restApiService.minioRequest(url, 'PUT', 'blob', image)),
            switchMap(() => restApiService.confirmPortfolioPhotoUpdated(portfolioId)),
            tap(() => {
              updateState(store, EProductsActions.UPDATE_PORTFOLIO_PHOTO_SUCCESS, ({ loadingProps, errorProps }) => ({
                ...withoutLoading(ELoadingEntity.UPDATE_PORTFOLIO_PHOTO, loadingProps),
                ...withoutError(ELoadingEntity.UPDATE_PORTFOLIO_PHOTO, errorProps),
              }));
            }),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.UPDATE_PORTFOLIO_PHOTO_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.UPDATE_PORTFOLIO_PHOTO, loadingProps),
                  ...withError(ELoadingEntity.UPDATE_PORTFOLIO_PHOTO, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const deleteInvestmentPhoto = rxMethod<{ portfolioId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.DELETE_INVESTMENT_PHOTO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.DELETE_INVESTMENT_PHOTO, loadingProps),
          }))
        ),
        switchMap(({ portfolioId }) =>
          restApiService.deleteInvestmentPhoto(portfolioId).pipe(
            tap(() => {
              updateState(store, EProductsActions.DELETE_INVESTMENT_PHOTO_SUCCESS, ({ loadingProps, errorProps }) => ({
                ...withoutLoading(ELoadingEntity.DELETE_INVESTMENT_PHOTO, loadingProps),
                ...withoutError(ELoadingEntity.DELETE_INVESTMENT_PHOTO, errorProps),
              }));
            }),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.DELETE_INVESTMENT_PHOTO_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.DELETE_INVESTMENT_PHOTO, loadingProps),
                  ...withError(ELoadingEntity.DELETE_INVESTMENT_PHOTO, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    // Instruments for new strategy or edited strategy with editedStrategyModelPortfolio
    const getAvailableInstruments = rxMethod<{ canReadEnglishOverride?: boolean, editedStrategyModelPortfolio?: IModelPortfolioOut, omitResponseFiltration?: boolean }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.AVAILABLE_INSTRUMENTS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.AVAILABLE_INSTRUMENTS, loadingProps),
          }))
        ),
        switchMap(({ canReadEnglishOverride, editedStrategyModelPortfolio, omitResponseFiltration }) =>
          restApiService.getInstruments(canReadEnglishOverride).pipe(
            map((response) =>
              updateState(store, EProductsActions.AVAILABLE_INSTRUMENTS_SUCCESS, ({ loadingProps, errorProps }) => {
                let filteredInstruments = [];
                if(omitResponseFiltration) {
                  filteredInstruments = response;
                } else {
                  const userInstrumentCategories = userDataService.userInstrumentCategories();
                  filteredInstruments = response.filter((i: IInstrumentDetailFullOut) => userInstrumentCategories
                      // Each instrument's category must be present in allowed instrument categories and instrument must not be marked for removal or as not insertable
                      .some(ic => ic === i.instrumentCategory) && !i.willBeRemoved && i.insertable
                      // Or it must be in the edited strategy's existing instruments
                      || editedStrategyModelPortfolio?.modelPortfolioInstruments.some(instrumentInfo => instrumentInfo.instrument.instrumentId === i.instrumentId)
                    ) as IInstrumentDetailFullOut[] ?? [];
                }
                return {
                availableInstruments: filteredInstruments,
                instrumentRegions: [...new Set(filteredInstruments.flatMap(instrument => instrument.regionalOrientations))],
                ...withoutLoading(ELoadingEntity.AVAILABLE_INSTRUMENTS, loadingProps),
                ...withoutError(ELoadingEntity.AVAILABLE_INSTRUMENTS, errorProps),
              }})
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.AVAILABLE_INSTRUMENTS_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.AVAILABLE_INSTRUMENTS, loadingProps),
                  ...withError(ELoadingEntity.AVAILABLE_INSTRUMENTS, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getPublicInstruments = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.PUBLIC_INSTRUMENTS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.PUBLIC_INSTRUMENTS, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getInstrumentsPublic().pipe(
            map((response) =>
              updateState(store, EProductsActions.PUBLIC_INSTRUMENTS_SUCCESS, ({ loadingProps, errorProps }) => {
                return {
                publicInstruments: response,
                publicInstrumentRegions: [...new Set(response.flatMap(instrument => instrument.regionalOrientations))],
                ...withoutLoading(ELoadingEntity.PUBLIC_INSTRUMENTS, loadingProps),
                ...withoutError(ELoadingEntity.PUBLIC_INSTRUMENTS, errorProps),
              }})
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.PUBLIC_INSTRUMENTS_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.PUBLIC_INSTRUMENTS, loadingProps),
                  ...withError(ELoadingEntity.PUBLIC_INSTRUMENTS, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getDraftInvestments = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.DRAFT_INVESTMENTS_LIST_REQUEST, ({ loadingProps, errorProps }) => ({
            ...withLoading(ELoadingEntity.DRAFT_INVESTMENTS_LIST, loadingProps),
            ...withoutError(ELoadingEntity.DRAFT_INVESTMENTS_LIST, errorProps),
            draftInvestments: [],
          }))
        ),
        switchMap(() => restApiService.getClientInvestmentDrafts().pipe(
          mergeMap(response => {
            const userInstrumentCategories = userDataService.userInstrumentCategories();
            const userMaxRiskScore = userDataService.maxRiskScore();
            const userManagementType = userDataService.userManagementType();

            if (!userMaxRiskScore || !userManagementType) {
              logger.error('User properties missing in drafts response handling!', { userMaxRiskScore, userManagementType });
            }
            return from(response.draftPortfolios.map(
              investmentDraft => ({
                ...investmentDraft,
                aml: response.aml,
                canAcceptDraft: getCanAcceptDraft(
                  investmentDraft.portfolioType, userManagementType, {
                  mpis: investmentDraft.modelPortfolioInstruments,
                  draftRiskScore: investmentDraft.riskScore,
                  allowedCats: userInstrumentCategories,
                  maxUserRiskScore: userMaxRiskScore
                }
                )
              })))
            }),
            // For each draft, if it has collaborator associated, attempt to fetch their photo
            mergeMap((investmentDraft: IDraftPortfolioOut) =>
              investmentDraft.collaborator.hasPhoto
                ? restApiService.getConsultImageForDraftInvestment(
                  investmentDraft.draftPortfolioId
                  ).pipe(
                    // On success convert image blob to URL format
                    map((photo) => ({
                      ...investmentDraft,
                      collaboratorPhoto: getPhotoUrlFromBlob(photo),
                    })),
                    // On error while fetching photo, return draft withotu img set, fallback will be used
                    catchError(() => of(({...investmentDraft, collaboratorPhoto: null }))),
                  )
                // If no collaborator, fallback will be used
                : of(({...investmentDraft, collaboratorPhoto: null }))
            ),
            toArray(),
            map(draftInvestments =>
              updateState(store, EProductsActions.DRAFT_INVESTMENTS_LIST_RESPONSE, ({ loadingProps, errorProps }) => ({
                  draftInvestments,
                  ...withoutLoading(ELoadingEntity.DRAFT_INVESTMENTS_LIST, loadingProps),
                  ...withoutError(ELoadingEntity.DRAFT_INVESTMENTS_LIST, errorProps),
                }))
              ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.DRAFT_INVESTMENTS_LIST_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.DRAFT_INVESTMENTS_LIST, loadingProps),
                  ...withError(ELoadingEntity.DRAFT_INVESTMENTS_LIST, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getInvestmentChangeDrafts = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.INVESTMENT_CHANGE_DRAFTS_LIST_REQUEST, ({ loadingProps, errorProps }) => ({
            ...withLoading(ELoadingEntity.INVESTMENT_CHANGE_DRAFTS_LIST, loadingProps),
            ...withoutError(ELoadingEntity.INVESTMENT_CHANGE_DRAFTS_LIST, errorProps),
            investmentChangeDrafts: [],
          }))
        ),
        switchMap(() => restApiService.getClientInvestmentChangeDrafts().pipe(
          map(response =>
            updateState(store, EProductsActions.INVESTMENT_CHANGE_DRAFTS_LIST_RESPONSE, ({ loadingProps, errorProps }) => ({
              investmentChangeDrafts: response.drafts.map(investmentChangeDraft => ({
                ...investmentChangeDraft,
                aml: response.aml,
              })),
              ...withoutLoading(ELoadingEntity.INVESTMENT_CHANGE_DRAFTS_LIST, loadingProps),
              ...withoutError(ELoadingEntity.INVESTMENT_CHANGE_DRAFTS_LIST, errorProps),
            }))
          ),
          catchError((error) =>
            of(
              updateState(store, EProductsActions.INVESTMENT_CHANGE_DRAFTS_LIST_FAILURE, ({ loadingProps, errorProps }) => ({
                ...withoutLoading(ELoadingEntity.INVESTMENT_CHANGE_DRAFTS_LIST, loadingProps),
                ...withError(ELoadingEntity.INVESTMENT_CHANGE_DRAFTS_LIST, { error, errorParams: {} }, errorProps),
              }))
            )
          )
        ))
      )
    );

    const getThematicStrategies = rxMethod<{rotation: boolean, limit?: number}>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.THEMATIC_STRATEGIES_REQUEST, ({ loadingProps, errorProps }) => ({
            ...withLoading(ELoadingEntity.THEMATIC_STRATEGIES, loadingProps),
            ...withoutError(ELoadingEntity.THEMATIC_STRATEGIES, errorProps),
          }))
        ),
        switchMap(({ rotation, limit }) =>
          (rotation && limit
            ? restApiService.getThematicStrategiesRotation(limit)
            : restApiService.getThematicStrategies()
          ).pipe(
            map((response) =>
              updateState(store, EProductsActions.THEMATIC_STRATEGIES_SUCCESS, ({ loadingProps, errorProps }) => ({
                thematicStrategies: response,
                ...withoutLoading(ELoadingEntity.THEMATIC_STRATEGIES, loadingProps),
                ...withoutError(ELoadingEntity.THEMATIC_STRATEGIES, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.THEMATIC_STRATEGIES_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.THEMATIC_STRATEGIES, loadingProps),
                  ...withError(ELoadingEntity.THEMATIC_STRATEGIES, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const clearThematicStrategies = () => {
      updateState(store, EProductsActions.CLEAR_THEMATIC_STRATEGIES, { thematicStrategies: [] });
    };

    const getCashInstruments = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.CASH_INSTRUMENTS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.CASH_INSTRUMENTS, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getCashInstruments().pipe(
            map((response) =>
              updateState(store, EProductsActions.CASH_INSTRUMENTS_SUCCESS, ({ loadingProps, errorProps }) => ({
                cashInstruments: response,
                ...withoutLoading(ELoadingEntity.CASH_INSTRUMENTS, loadingProps),
                ...withoutError(ELoadingEntity.CASH_INSTRUMENTS, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.CASH_INSTRUMENTS_FAILURE, ({ loadingProps, errorProps }) => ({
                  cashInstruments: [],
                  ...withoutLoading(ELoadingEntity.CASH_INSTRUMENTS, loadingProps),
                  ...withError(ELoadingEntity.CASH_INSTRUMENTS, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getStrategyLimits = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.STRATEGY_LIMITS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.STRATEGY_LIMITS, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getStrategyLimits().pipe(
            map((response) =>
              updateState(store, EProductsActions.STRATEGY_LIMITS_SUCCESS, ({ loadingProps, errorProps }) => ({
                strategyLimits: response,
                ...withoutLoading(ELoadingEntity.STRATEGY_LIMITS, loadingProps),
                ...withoutError(ELoadingEntity.STRATEGY_LIMITS, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.STRATEGY_LIMITS_FAILURE, ({ loadingProps, errorProps }) => ({
                  strategyLimits: null,
                  ...withoutLoading(ELoadingEntity.STRATEGY_LIMITS, loadingProps),
                  ...withError(ELoadingEntity.STRATEGY_LIMITS, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const clearDepositInfo = () => {
      updateState(store, EProductsActions.DEPOSIT_INFO_CLEAR, ({ errorProps }) => ({
        depositInfo: null,
        ...withoutError(ELoadingEntity.DEPOSIT_INFO, errorProps)
      }));
    }

    const clearDraftInvestments = () => {
      updateState(store, EProductsActions.DRAFT_INVESTMENTS_LIST_CLEAR, ({ errorProps }) => ({
        draftInvestments: [],
        ...withoutError(ELoadingEntity.DRAFT_INVESTMENTS_LIST, errorProps)
      }));
    }

    const clearInvestmentChangeDrafts = () => {
      updateState(store, EProductsActions.INVESTMENT_CHANGE_DRAFTS_LIST_CLEAR, ({ errorProps }) => ({
        investmentChangeDrafts: [],
        ...withoutError(ELoadingEntity.INVESTMENT_CHANGE_DRAFTS_LIST, errorProps)
      }));
    }

    const clearError = (loadingEntity: ELoadingEntity) => {
      updateState(store, '[Products] Clear Error', ({ errorProps }) => ({
        ...withoutError(loadingEntity, errorProps),
      }));
    }

    const getDepositInfo = rxMethod<{portfolioId: number}>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.DEPOSIT_INFO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.DEPOSIT_INFO, loadingProps),
          }))
        ),
        switchMap((portfolioId) =>
          restApiService.getMoneyDeposit(portfolioId).pipe(
            map((response) =>
              updateState(store, EProductsActions.DEPOSIT_INFO_SUCCESS, ({ loadingProps, errorProps }) => ({
                depositInfo: response,
                ...withoutLoading(ELoadingEntity.DEPOSIT_INFO, loadingProps),
                ...withoutError(ELoadingEntity.DEPOSIT_INFO, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.DEPOSIT_INFO_FAILURE, ({ loadingProps, errorProps }) => ({
                  depositInfo: null,
                  ...withoutLoading(ELoadingEntity.DEPOSIT_INFO, loadingProps),
                  ...withError(ELoadingEntity.DEPOSIT_INFO, { error, errorParams: { portfolioId } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getPhotoUrlFromBlob = (photo: Blob): string => {
      return photo ? URL.createObjectURL(photo) : '/assets/images/silhouette.png';
    }

    const withdrawAll = rxMethod<{ portfolioId: number, clientBankAccountId: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.WITHDRAWAL_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.WITHDRAWAL, loadingProps),
          }))
        ),
        switchMap(({ portfolioId, clientBankAccountId }) =>
          restApiService.withdrawAll(portfolioId, clientBankAccountId).pipe(
            tap(() => {
              updateState(store, EProductsActions.WITHDRAWAL_SUCCESS, ({ loadingProps, errorProps }) => ({
                ...withoutLoading(ELoadingEntity.WITHDRAWAL, loadingProps),
                ...withoutError(ELoadingEntity.WITHDRAWAL, errorProps),
              }));
            }),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.WITHDRAWAL_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.WITHDRAWAL, loadingProps),
                  ...withError(ELoadingEntity.WITHDRAWAL, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const withdrawInvestment = rxMethod<{ portfolioId: number, clientBankAccountId: number, amount: number }>(
      pipe(
        tap(() =>
          updateState(store, EProductsActions.WITHDRAWAL_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.WITHDRAWAL, loadingProps),
          }))
        ),
        switchMap(({ portfolioId, clientBankAccountId, amount }) =>
          restApiService.withdrawInvestment(portfolioId, clientBankAccountId, amount).pipe(
            tap(() => {
              updateState(store, EProductsActions.WITHDRAWAL_SUCCESS, ({ loadingProps, errorProps }) => ({
                ...withoutLoading(ELoadingEntity.WITHDRAWAL, loadingProps),
                ...withoutError(ELoadingEntity.WITHDRAWAL, errorProps),
              }));
            }),
            catchError((error) =>
              of(
                updateState(store, EProductsActions.WITHDRAWAL_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.WITHDRAWAL, loadingProps),
                  ...withError(ELoadingEntity.WITHDRAWAL, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getCanAcceptDraft = (
      draftPortfolioType: 'PORTFOLIO' | 'STRATEGY',
      managementType: EManagementType | null,
      { mpis, allowedCats, draftRiskScore, maxUserRiskScore }: IPortfolioDraftsCheckOptions
    ): boolean => {
      if (!managementType || !maxUserRiskScore) return false;
      if (draftPortfolioType === 'STRATEGY' && mpis && allowedCats) {
        return managementType === EManagementType.EXECUTION_ONLY && getStrategyInstrumentsAllowed(mpis, allowedCats);
      } else if (draftPortfolioType === 'PORTFOLIO' && draftRiskScore && maxUserRiskScore) {
        return draftRiskScore <= maxUserRiskScore;
      }
      return false;
    }

    const getStrategyInstrumentsAllowed = (mpis: IInvestmentModelInstrument[], allowedCats: EInstrumentCategory[]): boolean => {
      if (mpis && allowedCats) {
        try {
          const instrumentsCats = [...new Set(mpis.map(mpi => mpi.instrument.instrumentCategory))] as EInstrumentCategory[];
          if (allowedCats?.length && instrumentsCats?.length) {
            return instrumentsCats.every((ic) => allowedCats.includes(ic));
          }
        } catch (error) {
          logger.error('Error ocurred while checking if strategy draft instrument is allowed', error);
          return false;
        }
      }

      return false;
    }

    return {
      getPortfolioOverviews,
      getWithdrawalPortfolios,
      clearSelectedPortfolio,
      getSelectedPortfolio,
      getPortfolioFeeConfigs,
      getModelPortfolio,
      clearModelPortfolio,
      getModelStrategy,
      clearModelStrategy,
      getModelPortfolioByRiskScore,
      getPortfolioInstrumentRatios,
      getInvestmentInstrumentRemovals,
      clearInvestmentInstrumentRemovals,
      getPortfolioHistoryChartData,
      clearPortfolioHistoryChartData,
      getModelPortfolioFutureDevelopmentChart,
      getStrategyFutureDevelopmentChart,
      calculateEntryFeeForNewInvestment,
      createNewPortfolio,
      createNewStrategy,
      updatePortfolio,
      clearEntryFeeToDisplay,
      setNewInvestmentProgress,
      updateNewInvestmentProgress,
      clearNewInvestmentProgress,
      setEditInvestmentProgress,
      updateEditInvestmentProgress,
      clearEditInvestmentProgress,
      getAvailableInstruments,
      getPublicInstruments,
      getThematicStrategies,
      clearThematicStrategies,
      getDraftInvestments,
      getInvestmentChangeDrafts,
      getCashInstruments,
      getStrategyLimits,
      getAllInvestments,
      getBackcastingChart,
      clearBackcastingChart,
      getInstrumentChart,
      clearInstrumentChart,
      getDepositInfo,
      calculateEntryFeeForExistingInvestment,
      deleteInvestmentPhoto,
      updateStrategy,
      clearDepositInfo,
      withdrawAll,
      withdrawInvestment,
      clearError,
      clearDraftInvestments,
      clearInvestmentChangeDrafts,
      updateEfektikaStrategy,
    };
  })
) {}
